import {Observable, PureComputed} from "knockout";
import {FilesHelpers} from "../../../../helpers/files";

/**
 * Status types of an upload operation
 */
export enum FileUploadStatus {

    /**
     * File is selected and its details are fetched, but it is not yet uploaded
     */
    Initialized,

    /**
     * File is uploading
     */
    Uploading,

    /**
     * File is successfully uploaded
     */
    Uploaded,

    /**
     * File uploading is canceled by the user
     */
    Cancelled,

    /**
     * File upload failed
     */
    Failed,

    /**
     * File exceeds the maximum size
     */
    ExceedsMaxSize,

    /**
     * File's extension is not allowed to be uploaded
     */
    MismatchExtension,
    
    /**
     * The file needs at-least one video track but there is none
     */
    NoVideoTrack
}

/**
 * This class represents the file with all the basic upload data
 * like name, file type, the bytes blob etc.
 * This is the smallest unit of data for the uploader
 */
export class FileToUpload {

    /**
     * Name of the file
     * @private This is private because this is changed internally
     */
    private readonly _name: Observable<string>;

    /**
     * Returns the name of the file
     */
    public name: PureComputed<string>;

    /**
     * The total size of the file (in bytes)
     */
    public readonly totalSize: number;

    /**
     * The total size of the file (in MBs)
     */
    public readonly totalSizeStr: string;

    /**
     * Byte content or Blob of the file
     * This is the actual content to upload
     */
    public readonly fileContents: Blob;

    /**
     * Mime-Type of the file i.e. image/jpg, video/mp4 etc.
     */
    public readonly fileType: string;

    /**
     * Initialises the file object
     * @param name Name of the file
     * @param size The total size of the file (in bytes)
     * @param fileType Mime-Type of the file i.e. image/jpg, video/mp4 etc.
     * @param fileContents Byte content or Blob of the file
     */
    constructor(name: string, size: number, fileType: string, fileContents: Blob) {
        this._name = ko.observable<string>(name);
        this.name = ko.pureComputed(() => this._name());

        this.totalSize = size;
        this.totalSizeStr = FilesHelpers.getSizeStr('MB', size, 2);
        
        this.fileContents = fileContents;
        this.fileType = fileType;
    }

    /**
     * Get the file name extension. For example: It returns .jpg for TestImage.jpg
     */
    getFileExtension(): string {
        const fileName = this.name();
        if (system.isNullOrEmpty(fileName)) return "";
        
        const nameParts = fileName.split(".");
        if (nameParts.length <= 0) return "";
        
        return "." + nameParts[nameParts.length - 1];
    }
}